import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { WebsiteConfigFormExpansionPanelComponent } from '../website-config-form-expansion-panel/website-config-form-expansion-panel.component';
import { WebsiteStateService } from '../../../../../state-management/services/website-state.service';
import { BusinessConfiguration } from '../../../../../state-management/model/interfaces/state.model';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { v4 as uuidv4 } from 'uuid';
import { GenericDialog, DialogData } from '../website-config-form-page-administration/generic-dialog/generic-dialog.component';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '../../../../../services/logger.service';

interface PartnerLink {
  id?: string;
  isActive: boolean;
  url: string;
  name: string;
  imageUrl: string | null;
}

@Component({
  selector: 'app-website-config-form-partner',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatCheckbox,
    MatIcon,
    MatButtonModule,
    MatDividerModule,
    WebsiteConfigFormExpansionPanelComponent
  ],
  templateUrl: './website-config-form-partner.component.html',
  styleUrl: './website-config-form-partner.component.scss',
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebsiteConfigFormPartnerComponent implements OnInit {
  private formSubscription: Subscription = new Subscription();
  private debounceTimer: any;

  readonly dialog = inject(MatDialog);
  public metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
  public form!: FormGroup<{ partnerLinks: FormArray<FormGroup>; }>;

  constructor(
    private websiteState: WebsiteStateService,
    private logger: LoggerService
  ) { }

  ngOnInit(): void {
    const initialPartnerLinks = this.metaData().partnerLinks || [];
    this.logger.log('PARTNER FORM CHANGES: Initiale Partner-Links:', initialPartnerLinks);

    this.form = new FormGroup({
      partnerLinks: this.createPartnerLinksForm(initialPartnerLinks),
    });

    this.formSubscription = this.form.valueChanges.subscribe(() => {
      if (this.form.valid) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(async () => { this.#saveChanges(); }, 300);
      } else {
        this.logger.log('PARTNER FORM CHANGES: Formular ungültig, keine Daten gespeichert.', this.form);
      }
    });
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  private urlValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      // Check for http/https protocol
      const hasValidProtocol = /^https?:\/\//.test(control.value);

      if (!hasValidProtocol) {
        return { invalidUrl: 'URL muss mit http:// oder https:// beginnen' };
      }

      // Check for valid domain ending - domain should have at least one dot and end with valid characters
      const hasValidDomain = /^https?:\/\/(.+)\.[a-z]{2,}(\/.*)?$/i.test(control.value);

      if (!hasValidDomain) {
        return { invalidUrl: 'URL muss mit einer gültigen Domain enden (z.B. .com, .de, .org)' };
      }

      return null;
    };
  }

  private createPartnerLinksForm(partnerLinks: PartnerLink[]): FormArray {
    return new FormArray(partnerLinks.map(partner => this.createPartnerLinkGroup(partner)));
  }

  private createPartnerLinkGroup(partner: PartnerLink): FormGroup {
    return new FormGroup({
      isActive: new FormControl(partner.isActive !== undefined ? partner.isActive : true),
      url: new FormControl(partner.url || '', [Validators.required, this.urlValidator()]),
      name: new FormControl(partner.name || '', [Validators.required]),
      imageUrl: new FormControl(partner.imageUrl || null),
    });
  }

  get partnerLinksArray(): FormArray<FormGroup> {
    return this.form.get('partnerLinks') as FormArray<FormGroup>;
  }

  public addPartnerLink() {
    this.partnerLinksArray.push(this.createPartnerLinkGroup({
      isActive: true,
      url: 'https://',
      name: '',
      imageUrl: null,
    }));
  }

  public removePartnerLinkAtIndex(index: number): void {
    const formArray = this.partnerLinksArray as FormArray;
    if (index >= 0 && index < formArray.length) {
      formArray.removeAt(index);
      this.form.updateValueAndValidity();
      this.form.markAsDirty();
      // This forces Angular to run change detection in the next cycle
      setTimeout(() => { }, 0);
    }
  }

  async onDeletePartnerLink(index: number): Promise<void> {
    const partner: PartnerLink = this.partnerLinksArray.value[index];
    try {
      const confirmDelete = await this.openDeletePartnerDialog(partner.name);
      if (confirmDelete) {
        this.removePartnerLinkAtIndex(index);
      } else {
        this.logger.log(`Löschen des Partners ${partner.name} abgebrochen`);
      }
    } catch (error) {
      this.logger.error('Fehler beim Öffnen des Partner-Delete-Dialogs:', error);
    }
  }

  private openDeletePartnerDialog(partnerName: string): Promise<boolean> {
    const data: DialogData = {
      title: 'Warnung',
      textChunks: [
        { text: 'Soll der Partner ' },
        { text: ` ${partnerName} `, isStrong: true },
        { text: ' wirklich gelöscht werden?' }
      ],
      cancelButtonText: 'Abbrechen',
      confirmButtonText: 'Ja, löschen!',
    };

    const dialogRef = this.dialog.open(GenericDialog, {
      data: data,
      panelClass: "admin-theme"
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  #saveChanges() {
    this.logger.log('PARTNER FORM CHANGES: Speichere Änderungen', this.form.value);
    const partnerLinks = this.partnerLinksArray.value;
    this.websiteState.updateConfiguration({ partnerLinks });
  }
}
